import { create } from 'zustand'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'

// The People endpoint serves 25 rows per page (PeoplePagination). Kept here so
// the page count can be derived from the response's total.
export const PAGE_SIZE = 25

// People tab: one store backing all three segments, each a page of *people*.
//  - clients      = people with at least one won deal   (?type=client)
//  - leads        = people with at least one open one   (?type=lead)
//  - coldContacts = people with no opportunities at all (?type=contact)
//  - households   = /crm/households, same page / search / owner params
// A person can be both a client and a lead; they legitimately appear in both
// segments (each list is sourced independently server-side).
//
// Every segment reads exactly one backend page. The store used to walk every
// page of each list until has_more went false — 30+ sequential requests before
// the page could paint — which is what this replaces.
export const SEGMENTS = {
    clients: 'client',
    leads: 'lead',
    cold: 'contact',
    // Not people: its own endpoint, so no `type`.
    households: null,
}

const emptySegment = () => ({
    rows: [],
    // Rows matching the current query — what pagination divides up.
    count: 0,
    // Active column sort as {key, dir}, or null for the segment's own default
    // ordering. Kept per segment because the three tables share almost no
    // columns, and kept here rather than in the page so the refetches the
    // store already owns (search, promote, refreshCurrentPages) carry it
    // without every caller having to pass it along.
    sort: null,
    // Rows ignoring any search term. The tab badges have always shown the
    // segment's full size regardless of what's typed in the search box, so it
    // is tracked separately from `count` rather than replaced by it.
    total: 0,
    page: 1,
    loading: false,
    error: false,
})

const pageCount = (count) => Math.max(1, Math.ceil((count || 0) / PAGE_SIZE))

const peopleCount = (n) => `${n} ${n === 1 ? 'person' : 'people'}`

// One Select-mode mass action: POST, toast, then refetch the pages on screen,
// since people move between segments. Resolves to {ok, missing}, where
// `missing` is the ids a 400 named as unavailable; the page drops those.
// Errors are toasted by the axios response interceptor, so not here.
const runBulk = async (get, request, successMessage, search) => {
    try {
        const res = await request()
        toast.success(successMessage(res?.data || {}))
        await get().refreshCurrentPages(search)
        return { ok: true, missing: [] }
    } catch (e) {
        const data = e?.response?.data
        // bulk_delete's 400 lists ids; a validation 400 lists messages instead.
        const missing = (Array.isArray(data?.person_ids) ? data.person_ids : []).filter(Number.isInteger)
        if (missing.length) await get().refreshCurrentPages(search)
        return { ok: false, missing }
    }
}

export const useCrmPeopleStore = create((set, get) => ({
    clients: emptySegment(),
    leads: emptySegment(),
    cold: emptySegment(),
    households: emptySegment(),
    stages: [],
    // The CRM view's owner the rows were loaded for: 'all' (no `?owner=`) or a
    // user id. Set by fetchAll; every other request reuses it.
    owner: 'all',

    // Bumped per segment on every request so a slow response from a superseded
    // page or search term can be dropped instead of painting over a newer one.
    _tokens: { clients: 0, leads: 0, cold: 0, households: 0 },

    pageCountFor: (key) => pageCount(get()[key].count),

    fetchStages: async () => {
        try {
            const res = await fetcher.getCrmStages()
            const stages = (res?.data || [])
                .filter((s) => s.is_active)
                .sort((a, b) => a.order - b.order)
            set({ stages })
        } catch (e) {
            // The stage list only feeds "Make Lead"; losing it must not cost
            // the whole page, which loads its rows independently.
        }
    },

    // Load one page of one segment. `search` is sent to the backend so a match
    // on any page is found, rather than filtering the rows already on screen.
    fetchSegment: async (key, { page = 1, search = '' } = {}) => {
        const token = get()._tokens[key] + 1
        set((state) => ({
            _tokens: { ...state._tokens, [key]: token },
            [key]: { ...state[key], loading: true, error: false, page },
        }))

        const params = new URLSearchParams(
            SEGMENTS[key] ? { type: SEGMENTS[key], page: String(page) } : { page: String(page) }
        )
        const term = search.trim()
        if (term) params.set('search', term)
        const { owner } = get()
        if (owner !== 'all') params.set('owner', String(owner))
        // Omitted entirely while unsorted, so an untouched table asks for — and
        // the backend plans — exactly the request it always did.
        const sort = get()[key].sort
        if (sort) params.set('ordering', `${sort.dir === 'desc' ? '-' : ''}${sort.key}`)

        try {
            const res = await (key === 'households' ? fetcher.getCrmHouseholds(params) : fetcher.getCrmPeople(params))
            if (get()._tokens[key] !== token) return
            const { results = [], count = 0 } = res?.data || {}
            set((state) => ({
                [key]: {
                    ...state[key],
                    rows: results,
                    count,
                    // Only an unsearched response describes the whole segment.
                    total: term ? state[key].total : count,
                    page,
                    loading: false,
                    error: false,
                },
            }))
        } catch (e) {
            if (get()._tokens[key] !== token) return
            set((state) => ({ [key]: { ...state[key], loading: false, error: true } }))
            toast.error(key === 'households' ? 'Could not load households.' : 'Could not load people.')
        }
    },

    // Re-sort one segment. A new ordering is a new result set, so it starts
    // from page 1 — the old offset would land the user in the middle of it,
    // the same reason a new search term does.
    setSort: async (key, sort, search = '') => {
        set((state) => ({ [key]: { ...state[key], sort } }))
        await get().fetchSegment(key, { page: 1, search })
    },

    // Initial load and any search change: page 1 of all three segments, so the
    // tab badges are populated whichever tab is open — as they always have been.
    // A new `owner` (the CRM view) makes the rows on hand someone else's, so
    // they go now rather than when page 1 lands; each segment keeps its sort.
    fetchAll: async (search = '', owner = get().owner) => {
        if (owner !== get().owner) {
            set((state) => ({
                owner,
                ...Object.fromEntries(Object.keys(SEGMENTS).map((key) => [
                    key, { ...emptySegment(), sort: state[key].sort, loading: true },
                ])),
            }))
        }
        await Promise.all([
            get().fetchSegment('clients', { page: 1, search }),
            get().fetchSegment('leads', { page: 1, search }),
            get().fetchSegment('cold', { page: 1, search }),
            get().fetchSegment('households', { page: 1, search }),
        ])
    },

    // Refetch the pages currently on screen after a mutation. If a page emptied
    // because its last row moved segments, step back rather than showing a blank
    // table under a page number that no longer exists.
    refreshCurrentPages: async (search = '') => {
        const state = get()
        await Promise.all(
            Object.keys(SEGMENTS).map((key) =>
                get().fetchSegment(key, { page: state[key].page, search })
            )
        )
        await Promise.all(
            Object.keys(SEGMENTS).map((key) => {
                const segment = get()[key]
                if (segment.rows.length === 0 && segment.page > 1) {
                    return get().fetchSegment(key, {
                        page: Math.min(segment.page - 1, pageCount(segment.count)),
                        search,
                    })
                }
                return null
            })
        )
    },

    // Promote a cold contact by opening an opportunity for them — they leave the
    // cold list and appear under Leads / on the pipeline.
    promote: async (personId, search = '') => {
        const stage = get().stages[0]
        try {
            await fetcher.createCrmOpportunity({
                person: personId,
                stage: stage?.id || null,
                source: 'WEB',
                tier: 'WARM',
                status: 'OPEN',
            })
            toast.success('Moved to pipeline.')
            get().refreshCurrentPages(search)
        } catch (e) {
            toast.error('Could not promote contact.')
        }
    },

    // Select-mode mass actions — see runBulk.
    bulkDelete: (personIds, search = '') => runBulk(
        get,
        () => fetcher.bulkDeleteCrmPeople(personIds),
        ({ deleted = 0 }) => `Deleted ${peopleCount(deleted)}.`,
        search,
    ),

    bulkMarkDead: (personIds, search = '') => runBulk(
        get,
        () => fetcher.bulkMarkDeadCrmPeople(personIds),
        ({ people = 0 }) => `Marked ${peopleCount(people)} as dead.`,
        search,
    ),
}))
